import React, {FC, useEffect, useState, useMemo, useRef, useCallback} from 'react'
import {ID, UserAvatar} from '../../../../../_metronic/helpers'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useTheme, styled} from '@mui/material/styles'
import Popper, {PopperPlacementType} from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'
import Autocomplete, {
  AutocompleteCloseReason,
  autocompleteClasses,
} from '@mui/material/Autocomplete'
import InputBase from '@mui/material/InputBase'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import {useAuth} from '../../../auth'
import {useQuery} from 'react-query'
import {getTeamByInstance} from '../../../team/components/teams-list/core/_requests'

interface IconUserModel {
  name: string
  avatar?: string
  color?: string | null
  initials?: string
  task_responsible_id?: ID
  user_id?: ID
}

interface TeamOption {
  id: number
  name: string
  leader_id?: number
  leader?: { id: number; name: string }
  user?: Array<{
    id: number
    name: string
    avatar?: string
    job_role?: string
  }>
  isTeam: true
}

type AutocompleteOption = IconUserModel | TeamOption

type Props = {
  users: Array<IconUserModel>
  classes?: string
  onChange?: (e: any) => void
  hasDefaultValue?: boolean
  placement?: PopperPlacementType
  isReadonly?: boolean
  responsibles?: Array<IconUserModel>
  title?: string
  pendingUsers?: IconUserModel[]
}

interface PopperComponentProps {
  anchorEl?: any
  disablePortal?: boolean
  open: boolean
}

const StyledAutocompletePopper = styled('div')(({theme}) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: 13,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      padding: 8,
      borderBottom: `1px solid  ${theme.palette.mode === 'light' ? ' #eaecef' : '#30363d'}`,
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
        },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}))

function PopperComponent(props: PopperComponentProps) {
  const {disablePortal, anchorEl, open, ...other} = props
  return <StyledAutocompletePopper {...other} />
}
//@ts-ignore
const StyledPopper = styled(Popper)(({theme}) => ({
  border: `1px solid ${theme.palette.mode === 'light' ? '#e1e4e8' : '#30363d'}`,
  boxShadow: `0 8px 24px ${
    theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'
  }`,
  borderRadius: 6,
  width: 300,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
}))

const StyledInput = styled(InputBase)(({theme}) => ({
  padding: 10,
  width: '100%',
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
  '& input': {
    borderRadius: 4,
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#0d1117',
    padding: 8,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    border: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
    fontSize: 14,
    '&:focus': {
      boxShadow: '0px 0px 0px 3px rgba(181, 212, 78, 0.35)',
      borderColor: 'var(--tr-green-active)',
    },
  },
}))

const UsersSymbolGroup: FC<Props> = ({
  users,
  classes,
  onChange,
  hasDefaultValue = true,
  placement = 'top-end',
  isReadonly = false,
  responsibles = undefined,
  title,
  pendingUsers,
}) => {
  const {currentUser} = useAuth()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [value, setValue] = useState<IconUserModel[]>(users)
  const [pendingValue, setPendingValue] = useState<IconUserModel[]>([])
  const theme = useTheme()
  const open = Boolean(anchorEl)
  const popperContentRef = useRef<HTMLDivElement>(null)
  /** Texto digitado no campo de busca do Autocomplete (controlado para garantir filtro correto). */
  const [searchInputValue, setSearchInputValue] = useState('')

  // Forçar scroll no topo quando o dropdown abrir (após MUI aplicar seu scroll no item selecionado)
  const scrollListboxToTop = useCallback(() => {
    const container = popperContentRef.current
    if (!container) return
    const listbox = container.querySelector('[role="listbox"]') as HTMLElement | null
    if (listbox) listbox.scrollTop = 0
  }, [])

  useEffect(() => {
    if (!open) return
    scrollListboxToTop()
    const t1 = setTimeout(scrollListboxToTop, 0)
    const t2 = setTimeout(scrollListboxToTop, 50)
    const t3 = setTimeout(scrollListboxToTop, 100)
    const t4 = setTimeout(scrollListboxToTop, 200)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
      clearTimeout(t4)
    }
  }, [open, scrollListboxToTop])

  // Buscar times da instância apenas quando o dropdown estiver aberto
  const {data: teamsResponse} = useQuery(
    'teams-by-instance',
    () => getTeamByInstance(),
    {
      enabled: !!currentUser?.instance_id && !isReadonly && open,
      staleTime: 5 * 60 * 1000, // Cache por 5 minutos
      cacheTime: 10 * 60 * 1000, // Manter no cache por 10 minutos
      refetchOnWindowFocus: false,
    }
  )

  // A API retorna { teams: Team[], message: string, errors: boolean }
  const teams = teamsResponse?.teams 
    ? (Array.isArray(teamsResponse.teams) 
        ? teamsResponse.teams 
        : [teamsResponse.teams])
    : []

  // Criar estrutura de dados combinada (times + usuários)
  // Otimizado: só processa quando há times ou quando o dropdown está aberto
  const combinedOptions = useMemo(() => {
    // Se não há times carregados ainda, retornar apenas usuários
    if (!teams || teams.length === 0) {
      return users || []
    }

    const options: AutocompleteOption[] = []
    const usersInTeams = new Set<number>()
    const usersMap = new Map<number, IconUserModel>()
    
    // Criar mapa de usuários para busca O(1) ao invés de O(n)
    users?.forEach((user) => {
      const userId = Number(user.user_id)
      usersMap.set(userId, user)
    })
    
    // Adicionar times primeiro, seguidos pelos seus usuários
    teams.forEach((team) => {
      if (team.user && Array.isArray(team.user) && team.user.length > 0) {
        // Adicionar o time
        options.push({
          id: team.id!,
          name: team.name,
          leader_id: (team as any).leader_id,
          leader: (team as any).leader,
          user: team.user.map((u: any) => ({
            id: u.id,
            name: u.name,
            avatar: u.avatar,
            job_role: u.job_role,
          })),
          isTeam: true,
        })

        // Adicionar os usuários do time logo após o time (quem está em mais de um time aparece em cada um)
        team.user.forEach((user: any) => {
          usersInTeams.add(user.id)
          // Buscar o usuário completo de users usando mapa (mais rápido)
          const fullUser = usersMap.get(user.id)
          if (fullUser) {
            options.push(fullUser)
          } else {
            // Se não encontrar em users, criar um objeto básico
            options.push({
              user_id: user.id,
              name: user.name,
              avatar: user.avatar,
              color: user.job_role,
            })
          }
        })
      }
    })

    // Adicionar usuários que não estão em times
    users?.forEach((user) => {
      const userId = Number(user.user_id)
      if (!usersInTeams.has(userId)) {
        options.push(user)
      }
    })

    return options
  }, [teams, users])

  // Mapear usuários para seus times
  const userToTeamMap = useMemo(() => {
    const map = new Map<number, number>()
    teams.forEach((team) => {
      if (team.user && Array.isArray(team.user)) {
        team.user.forEach((user: any) => {
          map.set(user.id, team.id!)
        })
      }
    })
    return map
  }, [teams])

  // Verificar se todos os usuários de um time estão selecionados
  const isTeamFullySelected = (team: TeamOption): boolean => {
    if (!team.user || team.user.length === 0) return false
    const selectedUsers = responsibles ?? pendingValue
    return team.user.every((teamUser) =>
      selectedUsers.some((selectedUser) => 
        Number(selectedUser.user_id) === teamUser.id
      )
    )
  }

  useEffect(() => {
    if (!pendingUsers) setPendingValue([])
  }, [pendingUsers])

  const allUsersFlat = useMemo(() => users ?? [], [users])

  const nameMatchesQuery = useCallback((name: string, q: string) => {
    if (!name || !q) return false
    const lower = name.toLowerCase().trim()
    const qLower = q.toLowerCase().trim()
    if (!qLower) return false
    const words = lower.split(/\s+/).filter(Boolean)
    return lower.startsWith(qLower) || words.some((w) => w.startsWith(qLower))
  }, [])

  const filterOptions = useCallback(
    (opts: AutocompleteOption[], state: {inputValue: string}) => {
      const raw = (state.inputValue ?? '').trim()
      const query = raw.toLowerCase()
      if (!query) return opts
      if (query.length < 2) return []

      const matchingUsers = allUsersFlat.filter(
        (user) => user.name && nameMatchesQuery(user.name, query)
      )

      if (matchingUsers.length === 0) return []

      // Agrupar por equipe: cada usuário aparece só na primeira equipe a que pertence
      const result: AutocompleteOption[] = []
      const addedUserIds = new Set<number>()

      teams.forEach((team) => {
        if (!team.user || !Array.isArray(team.user) || team.user.length === 0) return

        const teamUserIds = new Set<number>(team.user.map((u: any) => Number(u.id)))

        const usersInTeam = matchingUsers.filter((u) => {
          const id = Number(u.user_id)
          return teamUserIds.has(id) && !addedUserIds.has(id)
        })

        if (usersInTeam.length === 0) return

        const teamOption: TeamOption = {
          id: team.id!,
          name: team.name,
          leader_id: (team as any).leader_id,
          leader: (team as any).leader,
          user: team.user.map((u: any) => ({
            id: u.id,
            name: u.name,
            avatar: u.avatar,
            job_role: u.job_role,
          })),
          isTeam: true,
        }

        usersInTeam.forEach((u) => addedUserIds.add(Number(u.user_id)))
        result.push(teamOption)
        result.push(...usersInTeam)
      })

      const standalone = matchingUsers.filter((u) => !addedUserIds.has(Number(u.user_id)))
      result.push(...standalone)

      return result
    },
    [allUsersFlat, nameMatchesQuery, teams]
  )

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isReadonly) return

    if (hasDefaultValue) {
      users.map((user) => {
        if (user.task_responsible_id == currentUser?.id && pendingValue.length == 0) {
          setPendingValue((value) => [...value, user])
        }
      })
    }

    setSearchInputValue('')
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setValue(pendingValue)
    setSearchInputValue('')
    if (anchorEl) {
      anchorEl.focus()
    }
    setAnchorEl(null)
  }

  useEffect(() => {
    if (!hasDefaultValue) return

    users.map((user) => {
      if (user.task_responsible_id == currentUser?.id && pendingValue.length == 0) {
        setPendingValue((value) => [...value, user])
        onChange &&
          onChange([
            {
              user_id: currentUser?.id,
              name: currentUser?.name!,
              avatar: currentUser?.avatar,
              color: null,
              task_responsible_id: currentUser?.id,
            },
          ])
      }
    })
  }, [])

  const intl = useIntl()

  return (
    <>
      <div className={`symbol-group symbol-hover ${classes}`} onClick={handleClick}>
        {(responsibles ?? pendingValue)?.length > 0
          ? (responsibles ?? pendingValue)?.map((user, i) => {
              return i < 3 ? (
                <OverlayTrigger
                  key={`${i}-${user.name}`}
                  placement='top'
                  overlay={<Tooltip id='tooltip-user-name'>{user.name}</Tooltip>}
                >
                  <UserAvatar
                    name={user.name}
                    avatar={user.avatar}
                    sizeClass='symbol-35px'
                    imgClassName='object-fit-cover'
                    style={{cursor: isReadonly ? 'default' : 'pointer'}}
                  />
                </OverlayTrigger>
              ) : null
            })
          : null}

        {(responsibles ?? pendingValue)?.length > 3 ? (
          <div className='symbol symbol-35px symbol-circle'>
            <span className='symbol-label bg-primary text-inverse-primary fw-bolder'>
              +{responsibles ? responsibles.length : pendingValue.length}
            </span>
          </div>
        ) : null}

        {!isReadonly && (
          <div className='symbol symbol-35px symbol-circle'>
            <span className='symbol-label bg-primary text-inverse-primary fw-bolder'>+</span>
          </div>
        )}
      </div>
      {/* 
      //@ts-ignore */}
      <StyledPopper
        id={value?.toString()}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        disablePortal={true}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div ref={popperContentRef}>
            <Box
              sx={{
                borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
                padding: '8px 10px',
                fontWeight: 600,
              }}
            >
              {title ?? intl.formatMessage({id: 'FORM.INPUT.NAME.ADDUSER_TASK'})}
            </Box>
            <Autocomplete
              open
              multiple
              inputValue={searchInputValue}
              onInputChange={(_, value, reason) => {
                // O MUI dispara "reset" ao selecionar opção; nesse caso, manter o texto de busca
                if (reason === 'reset') return
                setSearchInputValue(value ?? '')
              }}
              autoHighlight={false}
              onOpen={() => {
                requestAnimationFrame(() => {
                  requestAnimationFrame(scrollListboxToTop)
                })
              }}
              filterOptions={filterOptions}
              onClose={(event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
                if (reason === 'escape') {
                  handleClose()
                }
              }}
              value={responsibles ?? pendingValue}
              onChange={(event, newValue, reason, e) => {
                if (
                  event.type === 'keydown' &&
                  (event as React.KeyboardEvent).key === 'Backspace' &&
                  reason === 'removeOption'
                ) {
                  return
                }

                const option = e?.option as AutocompleteOption

                // Se for um time, processar todos os usuários do time
                if (option && 'isTeam' in option && option.isTeam) {
                  const teamOption = option as TeamOption
                  const wasSelected = isTeamFullySelected(teamOption)
                  const selectedUsers = responsibles ?? pendingValue

                  if (!wasSelected) {
                    // Adicionar todos os usuários do time
                    if (teamOption.user && teamOption.user.length > 0) {
                      const teamUsers: IconUserModel[] = teamOption.user.map((teamUser) => {
                        // Buscar o usuário completo de users
                        const fullUser = users?.find((u) => Number(u.user_id) === teamUser.id)
                        if (fullUser) {
                          return fullUser
                        }
                        // Se não encontrar, criar um objeto básico
                        return {
                          user_id: teamUser.id,
                          name: teamUser.name,
                          avatar: teamUser.avatar,
                          color: teamUser.job_role,
                        }
                      })

                      const newUsers = [...selectedUsers, ...teamUsers]
                      // Remover duplicatas
                      const uniqueUsers = newUsers.filter((user, index, self) =>
                        index === self.findIndex((u) => 
                          Number(u.user_id) === Number(user.user_id)
                        )
                      )

                      onChange && onChange(uniqueUsers)
                      setPendingValue(uniqueUsers)
                    }
                  } else {
                    // Remover todos os usuários do time
                    if (teamOption.user && teamOption.user.length > 0) {
                      const teamUserIds = new Set(teamOption.user.map((u) => u.id))
                      const filteredUsers = selectedUsers.filter(
                        (user) => !teamUserIds.has(Number(user.user_id))
                      )

                      onChange && onChange(filteredUsers)
                      setPendingValue(filteredUsers)
                    }
                  }
                  return
                }

                // Processamento normal para usuário individual
                onChange && onChange(newValue as IconUserModel[])
                setPendingValue(newValue as IconUserModel[])
              }}
              disableCloseOnSelect
              PopperComponent={PopperComponent}
              renderTags={() => null}
              noOptionsText='No labels'
              renderOption={(props, option, {selected, index}) => {
                const opt = option as AutocompleteOption

                // Se for um time
                if ('isTeam' in opt && opt.isTeam) {
                  const teamOption = opt as TeamOption
                  const teamSelected = isTeamFullySelected(teamOption)

                  const handleTeamToggle = (e: React.MouseEvent) => {
                    e.stopPropagation()
                    e.preventDefault()

                    const selectedUsers = responsibles ?? pendingValue

                    if (!teamSelected) {
                      // Adicionar apenas os usuários do time que AINDA NÃO estão selecionados
                      if (teamOption.user && teamOption.user.length > 0) {
                        const teamUsers: IconUserModel[] = teamOption.user.map((teamUser) => {
                          const fullUser = users?.find((u) => Number(u.user_id) === teamUser.id)
                          if (fullUser) {
                            return fullUser
                          }
                          return {
                            user_id: teamUser.id,
                            name: teamUser.name,
                            avatar: teamUser.avatar,
                            color: teamUser.job_role,
                          }
                        })

                        const missingUsers = teamUsers.filter(
                          (tu) => !selectedUsers.some((su) => Number(su.user_id) === Number(tu.user_id))
                        )
                        const newUsers = [...selectedUsers, ...missingUsers]
                        const uniqueUsers = newUsers.filter((user, index, self) =>
                          index === self.findIndex((u) => 
                            Number(u.user_id) === Number(user.user_id)
                          )
                        )

                        onChange && onChange(uniqueUsers)
                        setPendingValue(uniqueUsers)
                      }
                    } else {
                      // Remover apenas os usuários do time que ESTÃO selecionados
                      if (teamOption.user && teamOption.user.length > 0) {
                        const teamUserIds = new Set(teamOption.user.map((u) => u.id))
                        const filteredUsers = selectedUsers.filter(
                          (user) => !teamUserIds.has(Number(user.user_id))
                        )

                        onChange && onChange(filteredUsers)
                        setPendingValue(filteredUsers)
                      }
                    }
                  }

                  return (
                    <li {...props} key={`team-${teamOption.id}`}>
                      <Checkbox
                        checked={teamSelected}
                        sx={{mr: 1, p: 0.5}}
                        onClick={handleTeamToggle}
                      />
                      <Box
                        sx={{
                          flexGrow: 1,
                          fontWeight: 600,
                          fontSize: '13px',
                          color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
                          cursor: 'pointer',
                        }}
                        onClick={handleTeamToggle}
                      >
                        {teamOption.name}
                        {teamOption.user && (
                          <Box
                            component='span'
                            sx={{
                              ml: 1,
                              fontSize: '12px',
                              color: theme.palette.mode === 'light' ? '#586069' : '#8b949e',
                              fontWeight: 400,
                            }}
                          >
                            ({teamOption.user.length} {teamOption.user.length === 1 ? 'membro' : 'membros'})
                          </Box>
                        )}
                      </Box>
                    </li>
                  )
                }

                // Se for um usuário
                const userOption = opt as IconUserModel
                const selectedUsers = responsibles ?? pendingValue
                const isUserSelected = selectedUsers.some(
                  (user) => Number(user.user_id) === Number(userOption.user_id)
                )
                const teamId = userToTeamMap.get(Number(userOption.user_id))

                const handleUserToggle = (e: React.MouseEvent) => {
                  e.stopPropagation()
                  e.preventDefault()

                  if (isUserSelected) {
                    const filteredUsers = selectedUsers.filter(
                      (user) => Number(user.user_id) !== Number(userOption.user_id)
                    )
                    onChange && onChange(filteredUsers)
                    setPendingValue(filteredUsers)
                  } else {
                    const newUsers = [...selectedUsers, userOption]
                    onChange && onChange(newUsers)
                    setPendingValue(newUsers)
                  }
                }

                return (
                  <li
                    {...props}
                    key={`user-${userOption.user_id}-${index}`}
                    style={{
                      paddingLeft: teamId ? '40px' : '8px',
                    }}
                  >
                    <Checkbox
                      checked={isUserSelected}
                      sx={{mr: 1, p: 0.5}}
                      onClick={handleUserToggle}
                    />

                    <Box sx={{mr: 1, mt: '2px', flexShrink: 0}}>
                      <UserAvatar
                        name={userOption.name}
                        avatar={userOption.avatar}
                        sizeClass='symbol-20px'
                        symbolLabelClass='me-2'
                        imgClassName='object-fit-cover'
                      />
                    </Box>
                    <Box
                      sx={{
                        flexGrow: 1,
                        cursor: 'pointer',
                        '& span': {
                          color: theme.palette.mode === 'light' ? '#586069' : '#8b949e',
                        },
                      }}
                      onClick={handleUserToggle}
                    >
                      {userOption.name}
                    </Box>
                  </li>
                )
              }}
              options={combinedOptions}
              isOptionEqualToValue={(option, value) => {
                if ('isTeam' in option && option.isTeam) {
                  return 'isTeam' in value && value.isTeam && option.id === value.id
                }
                const opt = option as IconUserModel
                const val = value as IconUserModel
                return Number(opt.user_id) === Number(val.user_id)
              }}
              getOptionLabel={(option) => {
                if ('isTeam' in option && option.isTeam) {
                  return option.name
                }
                return (option as IconUserModel).name
              }}
              renderInput={(params) => (
                <StyledInput
                  inputRef={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  placeholder={intl.formatMessage({id: 'FORM.INPUT.NAME.SELECTUSER_TEAMS'})}
                />
              )}
            />
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </>
  )
}

export {UsersSymbolGroup}
